import React from "react";
import { ChevronDown } from "lucide-react";
import MarketHeroAvatar from "../../../../components/market/MarketHeroAvatar";
import ContractLineAvatar from "../../../../components/market/ContractLineAvatar";
import { MARKET_TYPES } from "../../../utils/bondPredictHelpers";
import {
  findContractLine,
  formatPrice01,
  getMarketTitle,
  getOrderOutcomeLabel,
  ORDER_STATUS_META,
} from "../../../utils/bondPredictPortfolioHelpers";
import { PortfolioOrderSideSign } from "./PortfolioListSign";
import { PortfolioOrderExpand } from "./PortfolioRowExpand";

const PortfolioOrdersTable = ({
  orders,
  expandedKey,
  onToggleExpand,
  userId,
  onTrade,
  onOpenMarket,
  onCancelOrder,
  cancelBusy,
}) => {
  if (!orders.length) return null;

  return (
    <div className="bp-port-list bp-port-list-orders" role="list" aria-label="Orders">
      {orders.map((order) => {
        const market = order?.market || {};
        const marketTitle = getMarketTitle(market);
        const key = String(order._id);
        const side = String(order?.side || "").toUpperCase();
        const statusKey = String(order?.status || "").toUpperCase();
        const statusMeta = ORDER_STATUS_META[statusKey] || {
          label: statusKey || "—",
          tone: "muted",
        };
        const quantity = Number(order?.quantity || 0);
        const filled = Number(order?.filledQuantity || 0);
        const fillPct = quantity > 0 ? Math.round(Math.min(1, filled / quantity) * 100) : 0;
        const fillTone = fillPct >= 100 ? "final" : fillPct > 0 ? "pending" : "live";
        const tradeTone = side === "BUY" ? "buy" : side === "SELL" ? "sell" : "neutral";
        const expanded = expandedKey === key;
        const outcomeLabel = getOrderOutcomeLabel(order);
        const typeLabel = String(order?.type || "LIMIT").toUpperCase();
        const price = typeof order?.price === "number" ? order.price : null;
        const isMulti = market?.type === MARKET_TYPES.MULTI_BINARY;
        const contractLine = isMulti
          ? findContractLine(market, order?.outcome?.selectionKey)
          : null;

        return (
          <article
            key={key}
            className={`bp-port-item bp-port-item-order bp-port-item-status-${statusMeta.tone}${expanded ? " bp-port-item-open" : ""}`}
            role="listitem"
          >
            <button
              type="button"
              className="bp-port-item-summary"
              aria-expanded={expanded}
              onClick={() => onToggleExpand(key, order)}
            >
              {market?.type === MARKET_TYPES.HEAD_TO_HEAD ? (
                <PortfolioOrderSideSign side={side} />
              ) : isMulti && contractLine ? (
                <ContractLineAvatar
                  market={market}
                  selectionKey={order?.outcome?.selectionKey}
                  label={outcomeLabel}
                  size={32}
                  className="bp-port-item-avatar"
                />
              ) : (
                <MarketHeroAvatar
                  market={market}
                  label={marketTitle}
                  size={32}
                  className="bp-port-item-avatar"
                />
              )}
              <span className="bp-port-item-copy">
                <strong className="bp-port-item-title">{marketTitle}</strong>
                <span className="bp-port-item-sub">
                  <span className={`bp-port-side-tag bp-port-side-tag-${tradeTone}`}>{side}</span>
                  <span className="bp-port-contract-chip">{outcomeLabel}</span>
                  <span className="bp-port-item-muted">{typeLabel}</span>
                  <span className="bp-port-item-muted tabular-nums">
                    {typeLabel === "MARKET" ? "at market" : `@ ${formatPrice01(price)}`}
                  </span>
                </span>
              </span>
              <span className="bp-port-item-summary-trail">
                <span className="bp-port-item-trail-head">
                  <span className="bp-port-item-trail-metric">
                    <span className="bp-port-item-metric-label">Filled</span>
                    <span className={`bp-port-item-fill tabular-nums bp-port-fill-${fillTone}`}>
                      {fillPct}%
                    </span>
                  </span>
                  <span className={`bp-port-status-pill bp-port-status-${statusMeta.tone}`}>
                    {statusMeta.label}
                  </span>
                </span>
                <span className="bp-port-item-fill-track" aria-hidden>
                  <span
                    className={`bp-port-item-fill-track-bar bp-port-fill-${fillTone}`}
                    style={{ width: `${fillPct}%` }}
                  />
                </span>
              </span>
              <ChevronDown
                size={14}
                className={`bp-port-item-chevron${expanded ? " bp-port-item-chevron-open" : ""}`}
                aria-hidden
              />
            </button>
            {expanded ? (
              <PortfolioOrderExpand
                order={order}
                userId={userId}
                onTrade={(side) => onTrade?.(side, order)}
                onOpenMarket={onOpenMarket}
                onCancelOrder={onCancelOrder}
                cancelBusy={cancelBusy}
              />
            ) : null}
          </article>
        );
      })}
    </div>
  );
};

export default PortfolioOrdersTable;
